/**
Copyright (C) 2022 by SeQuenX  - All Rights Reserved
This file is part of the ComplyVantage product development,
and is released under the "Commercial License Agreement".
You should have received a copy of the Commercial License Agreement license with
this file. If not, please write to: legal@sequenx.com, or visit www.sequenx.com
*/
import React from "react";
import { motion } from "framer-motion";
import { privacy_policy } from "../assets/svg";

const ServiceDetailItem = ({ title, description }) => (
  <motion.li
    initial={{ opacity: 0, y: 10 }}
    whileInView={{ opacity: 1, y: 0 }}
    transition={{ duration: 0.4 }}
    viewport={{ once: true }}
    className="text-gray-700 leading-8 mb-4"
  >
    {title}
    {description && (
      <p className="text-sm text-gray-500 font-normal">{description}</p>
    )}
  </motion.li>
);

const PrivacyPolicyDetails = () => {
  const serviceDetails = [
    {
      title:
        'Except through the "Contact" section, SeQuenX does not collect, nor use personal data via the website.',
    },
    {
      title:
        "To analyze data on the use of our website, SeQuenX collects anonymized data.",
    },
    {
      title:
        "SeQuenX has taken appropriate security measures to protect data and to prevent unauthorized access to data. This also applies to the company hosting and administering this website.",
    },
  ];

  const rightsDetails = [
    {
      title:
        "Being informed on which personal data we process and what we are using that data for;",
    },
    {
      title: "Having incorrect data corrected;",
    },
    {
      title: "Request to delete personal data;",
    },
    {
      title: "Revoke your consent to the processing (if applicable);",
    },
    {
      title: "Lodge a complaint with a supervisory authority",
    },
    {
      title: "Object to the use of personal data.",
    },
  ];

  return (
    <section className="py-20 bg-gradient-to-b from-gray-50 to-white">
      <div className="container mx-auto px-4">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          viewport={{ once: true }}
          className="text-center mb-16"
        >
          <h1 className="text-4xl font-bold text-gray-800 mb-4 ">
            Privacy Policy
          </h1>
          <div className="w-24 h-1 bg-blue-600 mx-auto"></div>
        </motion.div>

        <div className="flex flex-col md:flex-row items-center gap-12">
          <motion.div
            initial={{ opacity: 0, x: -20 }}
            whileInView={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.6, delay: 0.2 }}
            viewport={{ once: true }}
            className="w-full md:w-1/2"
          >
            <img
              src={privacy_policy}
              alt="Privacy Policy"
              className="w-full max-w-lg mx-auto rounded-lg shadow-xl transform hover:scale-105 transition-transform duration-300"
            />
          </motion.div>

          <motion.div
            initial={{ opacity: 0, x: 20 }}
            whileInView={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.6, delay: 0.3 }}
            viewport={{ once: true }}
            className="w-full md:w-1/2 space-y-6"
          >
            <div className="prose prose-lg">
              <p className="text-gray-700 leading-relaxed">
                SeQuenX B.V. complies with the requirements of the European
                General Data Protection Regulation (GDPR) and is responsible for
                all data processing on this website. In this privacy statement
                we outline how we process personal data collected through our
                website and how we use cookies:
                <br />
                In short:
              </p>
              <ul className="list-disc list-inside">
                {serviceDetails.map((detail, index) => (
                  <ServiceDetailItem
                    key={index}
                    title={detail.title}
                    description={detail.description}
                  />
                ))}
              </ul>
            </div>
          </motion.div>
        </div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.4 }}
          viewport={{ once: true }}
          className="mt-20"
        >
          <div className="bg-white rounded-xl shadow-lg p-8">
            <div className="prose prose-lg max-w-none">
              <h2 className="text-2xl font-bold text-gray-800 mb-6">
                Other Third Party Websites
              </h2>
              <p className="text-gray-700 leading-relaxed">
                Our website may contain links to third-party websites. We are
                not responsible for how these third parties operate or treat
                your personal information, so we recommend that you read the
                privacy policies and terms associated with these third-party
                websites carefully.
              </p>

              <h2 className="text-2xl font-bold text-gray-800 my-6">
                Inspection and Modification of your Data
              </h2>
              <p className="text-gray-700 leading-relaxed">
                You can contact us if you have any questions regarding our
                privacy policy or wish to review, modify or delete your personal
                data. In accordance with the GDPR you have a number of rights.
                Because the website does not store personal data, an appeal to
                these rights will not apply for data collected at the website.
                For information on our corporate privacy policy please contact
                SeQuenX.
              </p>

              <h2 className="text-2xl font-bold text-gray-800 my-6">
                Your rights in accordance with the GDPR:
              </h2>
              <ul className="list-disc list-inside">
                {rightsDetails.map((detail, index) => (
                  <ServiceDetailItem key={index} title={detail.title} />
                ))}
              </ul>
            </div>
          </div>
        </motion.div>
      </div>
    </section>
  );
};

export default PrivacyPolicyDetails;
